import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IVinAssociatedBooking } from '@app/models/booking-response.model';
import { ILoan } from '@app/models/loan.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Constants } from 'src/app/constants';
import { Cacheable } from 'ts-cacheable';

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  booking: ILoan;

  constructor(private http: HttpClient) {}

  // API Booking call
  public getBooking({ id }): Observable<ILoan> {
    return this.http.get<ILoan>(
      `${Constants.appConfiguration.apiRoot}/Bookings/${id}`
    );
  }

  /**
   * Converts booking
   * @param bookingId
   * @returns booking
   */

  public convertBooking(bookingId: number): Observable<ILoan> {
    return this.http.post<ILoan>(
      `${Constants.appConfiguration.apiRoot}/Proposals/ConvertBookingToProposal/${Constants.appConfiguration.clientApplicationId}/${bookingId}`,
      {
        bookingId,
        clientApplicationId: Constants.appConfiguration.clientApplicationId
      }
    );
  }

  public voidProductInBooking(bookingId: number, body): Observable<string> {
    return this.http.post<string>(
      `${Constants.appConfiguration.apiRoot}/Bookings/${bookingId}/Products/Void`,
      body
    );
  }

  public voidBooking(bookingId: number): Observable<string> {
    return this.http.post<string>(
      `${Constants.appConfiguration.apiRoot}/Bookings/${Constants.appConfiguration.clientApplicationId}/${bookingId}/Void`,
      {}
    );
  }

  public emailContractToBorrower(bookingId: number): Observable<string> {
    return this.http.post<string>(
      `${Constants.appConfiguration.apiRoot}/Documents/Booking/${bookingId}/EmailToBorrower`,
      {}
    );
  }

  /**
   * To email individual product contract
   * @param {number} bookingId
   * @param {[key: string]: boolean} params
   * @returns Observable
   */
  public emailIndividualContractToBorrower(
    bookingId: number,
    params: { [key: string]: boolean }
  ): Observable<string> {
    return this.http.post<string>(
      `${Constants.appConfiguration.apiRoot}/Documents/Booking/Product/${bookingId}/EmailToBorrower`,
      null,
      { params: params }
    );
  }

  public generateContract(
    bookingId: number,
    shouldIncludeSignatureTags: boolean
  ): Observable<Blob> {
    // Sets the url query component for signature tags
    const urlQueryComp = shouldIncludeSignatureTags
      ? '?IncludeSignatureTags=true'
      : '?IncludeSignatureTags=false';
    return this.http
      .get(
        `${Constants.appConfiguration.apiRoot}/Documents/Booking/${bookingId}${urlQueryComp}`,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        map((res: Blob) => {
          return new Blob([res], { type: 'application/pdf' });
        })
      );
  }

  /**
   * To generate Individual Contract
   * @param {number} bookingId
   * @param {[key: string]: boolean} params
   * @returns Observable
   */
  public generateIndividualContract(
    bookingId: number,
    params: { [key: string]: boolean }
  ): Observable<Blob> {
    return this.http
      .get(
        `${Constants.appConfiguration.apiRoot}/Documents/Product/${bookingId}`,
        { params: params, responseType: 'blob' as 'json' }
      )
      .pipe(
        map((res: Blob) => {
          return new Blob([res], { type: 'application/pdf' });
        })
      );
  }

  /**
   * To check If Booking Tied With FI And VIN
   * @param {number} portalAccountId
   * @param {string} vin
   * @returns {Observable<boolean>}
   */
  @Cacheable()
  public checkIfBookingTiedWithFIAndVIN(
    portalAccountId: number,
    vin: string
  ): Observable<boolean> {
    return this.http.get<boolean>(
      `${Constants.appConfiguration.apiRoot}/Bookings/Exists/${portalAccountId}/${vin}`
    );
  }

  /**
   * To get associated booking with the VIN info
   * @param {number} portalAccountId
   * @param {string} vin
   * @returns {Observable<IVinAssociatedBooking[]>}
   */
  public getAssociatedBookingWithVin(
    portalAccountId: number,
    vin: string
  ): Observable<IVinAssociatedBooking[]> {
    return this.http.get<IVinAssociatedBooking[]>(
      `${Constants.appConfiguration.apiRoot}/Bookings/VinBookings/${portalAccountId}/${vin}`
    );
  }
}
